import { useMemo } from "react";
import "./Button.css";
import { Icon } from "@/components/icons/Icon";
import type { IconName } from "@/components/icons/icons";
import { cn } from "@/lib/styles.utils";

export interface ButtonProps {
  label: string;
  type:
    | "primary-black"
    | "primary-red"
    | "primary-white"
    | "secondary-black"
    | "secondary-red"
    | "secondary-white"
    | "terciary-black"
    | "terciary-red"
    | "link";
  size?: "small" | "medium" | "medium-l" | "large";
  width?: string;
  disabled?: boolean;
  onClick?: (() => void) | ((data: any) => void);
  icon?: IconName;
  iconPosition?: "left" | "right";
  iconClassName?: string;
  buttonType?: "button" | "submit" | "reset";
  dataTestid?: string;
}

export const Button = ({
  size = "large",
  label,
  disabled = false,
  type = "primary-red",
  onClick = () => {},
  width,
  icon,
  iconPosition,
  iconClassName = "",
  buttonType = "button",
  dataTestid,
}: ButtonProps) => {
  const iconColor = useMemo(() => {
    switch (type) {
      case "primary-black":
        return "text-ib-pt-color-white";
      case "primary-red":
        return "text-ib-pt-color-white";
      case "primary-white":
        if (disabled) return "text-ib-gray-disabled";
        return "text-ib-pt-color-red-500 group-hover:text-ib-pt-color-red-800";
      case "secondary-black":
        if (disabled) return "text-ib-pt-color-gray-500";
        return "text-ib-pt-color-black group-hover:text-ib-pt-color-gray-800";
      case "secondary-red":
        if (disabled) return "ib-pt-color-gray-500";
        return "ib-pt-color-red-500 group-hover:text-ib-pt-color-red-800";
      case "terciary-black":
        if (disabled) return "ib-pt-color-gray-500";
        return "ib-pt-color-black group-hover:text-ib-pt-color-gray-800";
      case "terciary-red":
        if (disabled) return "ib-pt-color-gray-500";
        return "ib-pt-color-red-500 group-hover:text-ib-pt-color-red-800";
      case "link":
        if (disabled) return "ib-pt-color-gray-500";
        return "ib-pt-color-black group-hover:text-ib-pt-color-gray-800";
    }
  }, [type, disabled]);

  return (
    <button
      data-testid={dataTestid}
      type={buttonType}
      className={`flex group items-center ${
        iconPosition === "right" ? "flex-row-reverse" : "flex-row"
      } ${size}-button ${type}-button`}
      disabled={disabled}
      onClick={onClick}
      style={{ width: width || "100%" }}
    >
      {icon ? (
        <div className={cn(iconColor)}>
          <Icon name={icon} className={cn("size-4", iconClassName)} />
        </div>
      ) : null}
      <span className="text-nowrap">{label}</span>
    </button>
  );
};

export default Button;
